import { useState } from "react";
import PageHero from "@/components/PageHero";
import api, { formatApiError } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/brand";
import { CheckCircle2, AlertCircle, Building2, GraduationCap, Globe, Briefcase } from "lucide-react";

const TYPES = [
  ["independent", "Independent Organisation", Building2],
  ["school", "School", GraduationCap],
  ["university", "University", GraduationCap],
  ["district", "District Unit", Globe],
  ["state", "State Unit", Globe],
  ["zonal", "Zonal Body", Globe],
  ["academy", "Academy", Building2],
  ["club", "Club", Building2],
  ["corporate", "Corporate Body", Briefcase],
];

export default function Affiliations() {
  const [form, setForm] = useState({
    type: "academy", organisation_name: "", contact_person: "", email: "", phone: "",
    state: "", district: "", address: "", established_year: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const payload = { ...form, established_year: form.established_year ? Number(form.established_year) : null };
      const { data } = await api.post("/affiliations", payload);
      setSuccess(data); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div data-testid="affiliation-success">
        <PageHero eyebrow="Affiliation" title="Affiliation Application Submitted" />
        <section className="nkf-container py-12">
          <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-md p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
            <h2 className="font-display text-2xl text-nkf-blue mt-4">Affiliation Application Received</h2>
            <div className="mt-5 inline-block bg-nkf-gold/15 border border-nkf-gold/40 px-4 py-3 rounded">
              <div className="text-[11px] uppercase tracking-widest text-nkf-blue">Application Number</div>
              <div className="font-display text-2xl text-nkf-blue mt-1">{success.application_no}</div>
            </div>
            <p className="text-xs text-slate-500 mt-4">Our affiliation committee will review your application within 14 working days.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="affiliations-page">
      <PageHero eyebrow="Affiliations" title="Affiliate with NKF Bharat" subtitle="Schools, universities, academies, clubs, state and district bodies — apply to become an officially affiliated entity of the national federation." />
      <section className="section-spacing nkf-container">
        <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="affiliation-form">
          {error && <div className="md:col-span-2 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {error}</div>}
          <label className="md:col-span-2 block">
            <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-2 font-semibold">Affiliation Type</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {TYPES.map(([k, l, Ic]) => (
                <button type="button" key={k} onClick={() => set("type", k)} data-testid={`aff-type-${k}`} className={`p-3 border rounded-md text-xs text-left transition ${form.type === k ? "bg-nkf-blue text-white border-nkf-blue" : "bg-white border-slate-200 hover:border-nkf-blue/40 text-slate-700"}`}>
                  <Ic className={`h-4 w-4 mb-1.5 ${form.type === k ? "text-nkf-gold" : "text-nkf-red"}`} />
                  <div className="font-semibold uppercase tracking-wide">{l}</div>
                </button>
              ))}
            </div>
          </label>
          <Input label="Organisation Name *" required value={form.organisation_name} set={(v) => set("organisation_name", v)} />
          <Input label="Contact Person *" required value={form.contact_person} set={(v) => set("contact_person", v)} />
          <Input label="Email *" type="email" required value={form.email} set={(v) => set("email", v)} />
          <Input label="Phone *" required value={form.phone} set={(v) => set("phone", v)} />
          <Select label="State *" required value={form.state} set={(v) => set("state", v)} options={INDIAN_STATES} />
          <Input label="District *" required value={form.district} set={(v) => set("district", v)} />
          <Input label="Established Year" value={form.established_year} set={(v) => set("established_year", v)} placeholder="e.g. 2014" />
          <Input label="Address" value={form.address} set={(v) => set("address", v)} wide />
          <Input label="Notes" value={form.notes} set={(v) => set("notes", v)} wide />
          <div className="md:col-span-2 flex justify-end mt-2">
            <button disabled={submitting} data-testid="submit-affiliation" className="bg-nkf-blue text-white uppercase tracking-widest text-sm font-bold px-6 py-3 rounded hover:bg-nkf-navy disabled:opacity-60 transition">
              {submitting ? "Submitting…" : "Submit Affiliation"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue";

function Input({ label, value, set, required, wide, type = "text", placeholder }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-1.5 font-semibold">{label}</span>
      <input required={required} type={type} placeholder={placeholder} value={value} onChange={(e) => set(e.target.value)} className={inputCls} />
    </label>
  );
}
function Select({ label, value, set, options, required }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-1.5 font-semibold">{label}</span>
      <select required={required} value={value} onChange={(e) => set(e.target.value)} className={inputCls}>
        <option value="">Select</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
